/**
 * Same rules as the product page: interpolate between tiers, or scale from a single tier.
 * amounts are paise; rent_days are positive integers.
 */

export const STANDARD_RENT_TIERS = [1, 3, 5, 7];

/** Total rental price in ₹ from product_prices; interpolates / scales when no exact rent_days row. */
export function getRentalPriceRupees(productPrices, days) {
  if (!productPrices?.length || days < 1) return 0;
  const sorted = [...productPrices].sort((a, b) => a.rent_days - b.rent_days);
  const exact = sorted.find((p) => p.rent_days === days);
  if (exact) return exact.amount_paise / 100;
  const lower = sorted.filter((p) => p.rent_days < days).pop();
  const upper = sorted.find((p) => p.rent_days > days);
  if (lower && upper) {
    const t = (days - lower.rent_days) / (upper.rent_days - lower.rent_days);
    const paise = lower.amount_paise + t * (upper.amount_paise - lower.amount_paise);
    return Math.round(paise) / 100;
  }
  if (upper && !lower) {
    const perDay = upper.amount_paise / upper.rent_days;
    return Math.round(perDay * days) / 100;
  }
  if (lower && !upper) {
    const perDay = lower.amount_paise / lower.rent_days;
    return Math.round(perDay * days) / 100;
  }
  const only = sorted[0];
  return Math.round((only.amount_paise * days) / only.rent_days) / 100;
}

/** Total price in paise (integer). */
export function getRentalPricePaise(productPrices, days) {
  const rupees = getRentalPriceRupees(productPrices, days);
  return Math.round(rupees * 100);
}

/**
 * Build the four standard admin rows (1/3/5/7 days). Uses DB rows when present;
 * otherwise fills with the same amounts the storefront would charge.
 */
export function buildStandardAdminPriceTiers(pricesFromApi) {
  const list = (pricesFromApi || [])
    .map((p) => ({
      rent_days: Number(p.rent_days),
      amount_paise: Number(p.amount_paise),
    }))
    .filter((p) => Number.isInteger(p.rent_days) && p.rent_days >= 1 && !Number.isNaN(p.amount_paise) && p.amount_paise > 0);

  if (list.length === 0) {
    return STANDARD_RENT_TIERS.map((rent_days) => ({ rent_days, amount_paise: 0 }));
  }

  const rows = STANDARD_RENT_TIERS.map((rent_days) => {
    const exact = list.find((p) => p.rent_days === rent_days);
    if (exact) return { rent_days, amount_paise: exact.amount_paise };
    return { rent_days, amount_paise: getRentalPricePaise(list, rent_days) };
  });

  const extra = list
    .filter((p) => !STANDARD_RENT_TIERS.includes(p.rent_days))
    .sort((a, b) => a.rent_days - b.rent_days)
    .map((p) => ({ rent_days: p.rent_days, amount_paise: p.amount_paise }));

  return [...rows, ...extra];
}
